import React, { useEffect, useState } from 'react';
import {
  Box,
  Flex,
  Text,
  Stat,
  StatLabel,
  StatNumber,
  SimpleGrid,
  Heading,
  VStack,
  HStack,
  Divider,
  useColorModeValue,
  Input,
  Button
} from '@chakra-ui/react';
import { Bar } from 'react-chartjs-2';
import 'chart.js/auto';
import api from '../api'; // adjust path as needed

const Dashboard = () => {
  const [totalEarned, setTotalEarned] = useState(0);
  const [earnData, setEarnData] = useState([]);
  const [topProducts, setTopProducts] = useState([]);
  const [lessProducts, setLessProducts] = useState([]);
  const [startDate, setStartDate] = useState('2025-01-01');
  const [endDate, setEndDate] = useState('2025-04-30');

  const fetchDashboardData = async () => {
    try {
      const totalEarnedRes = await api.get(`/total_earned/?start_date=${startDate}&end_date=${endDate}`);
      setTotalEarned(totalEarnedRes.data.total_earned);
    } catch (error) {
      console.error('Failed to fetch total earned:', error);
    }

    try {
      const earnDataRes = await api.get(`/earned_graphic_data/?start_date=${startDate}&end_date=${endDate}`);
      setEarnData(earnDataRes.data);
    } catch (error) {
      console.error('Failed to fetch earnings graph data:', error);
    }

    try {
      const topProductsRes = await api.get('/statistics/top-sold-products/');
      setTopProducts(topProductsRes.data);
    } catch (error) {
      console.error('Failed to fetch top sold products:', error);
    }

    try {
      const lessProductsRes = await api.get('/statistics/less-sold-products/');
      setLessProducts(lessProductsRes.data);
    } catch (error) {
      console.error('Failed to fetch less sold products:', error);
    }
  };

  useEffect(() => {
    fetchDashboardData();
  }, []);

  const getDateRangeLabels = () => {
    const start = new Date(startDate);
    const end = new Date(endDate);
    const dates = [];
    let current = new Date(start);
    while (current <= end) {
      dates.push(current.toISOString().split('T')[0]);
      current.setDate(current.getDate() + 1);
    }
    return dates;
  };

  const chartLabels = getDateRangeLabels();

  const chartDataMap = earnData.reduce((acc, item) => {
    acc[item.date] = item.total_earned;
    return acc;
  }, {});

  const chartData = chartLabels.map(date => chartDataMap[date] || 0);

  const chartConfig = {
    labels: chartLabels,
    datasets: [
      {
        label: `Графік прибутку (з ${startDate} по ${endDate})`,
        data: chartData,
        backgroundColor: '#805AD5',
      },
    ],
  };

  const statBoxBg = useColorModeValue('gray.100', 'gray.700');

  return (
    <Box p={5}>
      <Heading mb={6}>Статистика</Heading>

      <Flex gap={2} wrap="wrap" mb={6} align="center">
        <Input type="date" size="sm" w="160px" value={startDate} onChange={e => setStartDate(e.target.value)} />
        <Input type="date" size="sm" w="160px" value={endDate} onChange={e => setEndDate(e.target.value)} />
        <Button colorScheme="purple" size="sm" onClick={fetchDashboardData}>Застосувати</Button>
      </Flex>

      <SimpleGrid columns={{ base: 1, md: 3 }} spacing={5} mb={10}>
        <Stat bg={statBoxBg} p={5} borderRadius="lg">
          <StatLabel>Всього зароблено</StatLabel>
          <StatNumber>{totalEarned} грн</StatNumber>
        </Stat>
      </SimpleGrid>

      <Box bg={statBoxBg} p={5} borderRadius="lg" mb={10}>
        <Text fontSize="xl" mb={4}>Графік прибутку (з {startDate} по {endDate})</Text>
        <Bar data={chartConfig} />
      </Box>

      <SimpleGrid columns={{ base: 1, md: 2 }} spacing={5}>
        <Box bg={statBoxBg} p={5} borderRadius="lg">
          <Text fontSize="xl" mb={4}>Найбільш продавані продукти</Text>
          <VStack align="start">
            {topProducts.map((product, idx) => (
              <Box key={idx} w="100%">
                <HStack justify="space-between">
                  <Text>{product.product}</Text>
                  <Text fontWeight="bold">{product.total_revenue} грн</Text>
                </HStack>
                <Text fontSize="sm" color="gray.500">Продано: {product.amount_sold}</Text>
                <Divider my={2} />
              </Box>
            ))}
          </VStack>
        </Box>

        <Box bg={statBoxBg} p={5} borderRadius="lg">
          <Text fontSize="xl" mb={4}>Найменш продавані продукти</Text>
          <VStack align="start">
            {lessProducts.map((product, idx) => (
              <Box key={idx} w="100%">
                <HStack justify="space-between">
                  <Text>{product.product}</Text>
                  <Text fontWeight="bold">{product.total_revenue} грн</Text>
                </HStack>
                <Text fontSize="sm" color="gray.500">Продано: {product.amount_sold}</Text>
                <Divider my={2} />
              </Box>
            ))}
          </VStack>
        </Box>
      </SimpleGrid>
    </Box>
  );
};

export default Dashboard;